import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "react-oidc-context";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";
import PricingDescription from "../pricing_page/PricingDescription";

// ─── Prices ──────────────────────────────────────────────────────────────────
const PRICES = {
  monthly: { amount: 13.75, price_id: "price_1TJmAhJcvMflQ4Co0oi4rwVf" },
  annual: { amount: 99, price_id: "price_1TJmDKJcvMflQ4Cot4yYRhva" },
};

const MONTHLY_EQUIV = (PRICES.annual.amount / 12).toFixed(2);
const SAVINGS_PCT = Math.round(
  (1 - PRICES.annual.amount / 12 / PRICES.monthly.amount) * 100,
);
const SAVINGS_AMT = Math.round(
  PRICES.monthly.amount * 12 - PRICES.annual.amount,
);

// ─── Features ────────────────────────────────────────────────────────────────
const FEATURES = [
  "25+ data broker & people-search sites",
  "Real-time dashboard & request tracking",
  "Continuous monitoring & re-engagement requests",
];

// ─── Tokens ──────────────────────────────────────────────────────────────────
const cyan = "#12b2f1";
const orange = "#F59E0B";
const orangeHov = "#D97706";
const ink = "#111827";
const muted = "#6B7280";
const border = "#E5E7EB";
const green = "#16a34a";

// ─── Main ─────────────────────────────────────────────────────────────────────
const PricingBox = ({ showHeader = true, backgroundColor = "#ffffff" }) => {
  const [billing, setBilling] = useState("annual");
  const [loadingPriceId, setLoading] = useState(null);
  const [errorMessage, setError] = useState("");
  const { user: authUser, isAuthenticated, isLoading: authLoading } = useAuth();

  const { data, isLoading, isFetching } = useUserQuery({
    enabled: isAuthenticated,
  });
  const user = data?.user;

  const subscriptionStatus = user?.stripe_data?.subscription?.toLowerCase?.();
  const isSubscribed = subscriptionStatus === "active";
  const subscriptionUnknown =
    authLoading || (isAuthenticated && (isLoading || isFetching || !user));

  // Derived button appearance
  const buttonBg = subscriptionUnknown || isSubscribed ? "#E5E7EB" : orange;
  const buttonColor = subscriptionUnknown || isSubscribed ? muted : "black";
  const buttonHoverBg =
    subscriptionUnknown || isSubscribed ? "#E5E7EB" : orangeHov;

  const { price_id } = PRICES[billing];

  const displayAmount = billing === "annual" ? MONTHLY_EQUIV : "13.75";
  const [whole, cents] = displayAmount.split(".");

  const handleSubscribe = async () => {
    if (!isAuthenticated || !authUser?.profile?.email) {
      localStorage.setItem("login_intent", "from_header");
      localStorage.setItem("pending_price_id", price_id);
      window.location.href = "/signup";
      return;
    }
    setLoading(price_id);
    setError("");
    try {
      const res = await fetch("https://api.kloudscan.com/stripe_checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authUser?.access_token}`,
        },
        body: JSON.stringify({
          line_items: [{ price: price_id, quantity: 1 }],
          customer_email: authUser?.profile?.email,
        }),
      });
      if (res.status === 409) {
        const err = await res.json();
        setError(err.error || "You already have an active subscription.");
        return;
      }
      if (!res.ok) {
        const err = await res.json();
        setError(err.error || "Unable to start checkout. Please try again.");
        return;
      }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError("Unable to start checkout. Please try again.");
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        textAlign: "left",
        width: "100%",
        px: { xs: 2, sm: 3, md: 4 },
        boxSizing: "border-box",
      }}
    >
      {/* ── Pricing Description: flush left ────────────────────────────── */}
      {showHeader && (
        <Box sx={{ maxWidth: 1200, mb: 5 }}>
          <PricingDescription />
        </Box>
      )}

      {/* ── Billing toggle ─────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
        <Box
          sx={{
            display: "inline-flex",
            bgcolor: "#F3F4F6",
            borderRadius: "12px",
            p: "4px",
            gap: "4px",
          }}
        >
          {["monthly", "annual"].map((mode) => {
            const active = billing === mode;
            return (
              <Box
                key={mode}
                onClick={() => setBilling(mode)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2.5,
                  py: 0.9,
                  borderRadius: "9px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  transition: "all 0.2s",
                  userSelect: "none",
                  bgcolor: active ? "#ffffff" : "transparent",
                  color: active ? ink : muted,
                  boxShadow: active ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                }}
              >
                {mode === "monthly" ? "Monthly" : "Annual"}
                {mode === "annual" && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 800,
                      bgcolor: active ? cyan : "rgba(18,178,241,0.12)",
                      color: active ? "#fff" : cyan,
                      px: 0.9,
                      py: 0.25,
                      borderRadius: "5px",
                      transition: "all 0.2s",
                    }}
                  >
                    -{SAVINGS_PCT}%
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      <Collapse in={!!errorMessage}>
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 540, mx: "auto", borderRadius: 3 }}
          action={
            <IconButton size="small" onClick={() => setError("")}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {errorMessage}
        </Alert>
      </Collapse>

      {/* ── Card: centered ────────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
          borderRadius: "2px",
          border: `1.5px solid ${border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          textAlign: "left",
          bgcolor: "#ffffff",
        }}
      >
        {/* Left: price + CTA */}
        <Box
          sx={{
            flex: "0 0 360px",
            p: { xs: 3.5, sm: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            borderRight: { md: `1.5px solid ${border}` },
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 28,
              color: ink,
              lineHeight: 1.1,
              mb: 0.75,
            }}
          >
            Standard
          </Typography>

          {/* Price */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: ink,
                  mt: 1,
                  mr: 0.25,
                }}
              >
                $
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 70,
                  fontWeight: 900,
                  lineHeight: 1,
                  color: ink,
                  letterSpacing: "-3px",
                }}
              >
                {whole}
              </Typography>
              <Box sx={{ ml: 0.5, mt: 1.5 }}>
                <Typography
                  component="div"
                  sx={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: ink,
                    lineHeight: 1,
                  }}
                >
                  {cents}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: 12, color: muted, fontWeight: 500 }}
                >
                  /month
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color: billing === "annual" ? green : cyan,
                transition: "color 0.2s",
              }}
            >
              {billing === "annual"
                ? `Save $${SAVINGS_AMT} a year`
                : `Switch to annual — save ${SAVINGS_PCT}%`}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: muted,
                display: "block",
                mt: 0.25,
                visibility: billing === "annual" ? "visible" : "hidden",
              }}
            >
              Billed as ${PRICES.annual.amount}/year
            </Typography>
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={
              loadingPriceId === price_id || isSubscribed || subscriptionUnknown
            }
            onClick={handleSubscribe}
            sx={{
              borderRadius: "10px",
              bgcolor: buttonBg,
              color: buttonColor,
              fontWeight: 800,
              fontSize: 16,
              textTransform: "none",
              py: 1.65,
              boxShadow: "none",
              "&:hover": {
                bgcolor: buttonHoverBg,
                boxShadow: "none",
              },
              transition: "background 0.2s",
            }}
          >
            {subscriptionUnknown || loadingPriceId === price_id ? (
              <CircularProgress size={22} color="inherit" />
            ) : isSubscribed ? (
              "Already subscribed"
            ) : (
              "Protect your data"
            )}
          </Button>

          <Typography
            variant="caption"
            sx={{
              textAlign: "center",
              color: muted,
              display: "block",
              mt: -0.5,
            }}
          >
            {isSubscribed
              ? "You're already subscribed to a plan"
              : "30-day money back guarantee"}
          </Typography>
        </Box>

        {/* Right: features */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#FAFAFA",
            p: { xs: 3.5, sm: 4 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: ink }}>
            What's included
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {FEATURES.map((item, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
              >
                <CheckIcon sx={{ fontSize: 17, color: cyan, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{ color: muted, lineHeight: 1.5 }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingBox;
